import { ChevronRight } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";
import { MenuItem } from "./MenuItem";

export interface MenuFlyoutProps {
  icon?: ReactNode;
  children: ReactNode;

  /** The current value shown before the chevron, e.g. "Medium". Omit for a plain browse/expand row. */
  value?: ReactNode;

  /** The nested popover's own content, typically a MenuSection of MenuOptions. */
  flyout: ReactNode;

  disabled?: boolean;
  size?: "sm" | "md";
}

export function MenuFlyout({
  icon,
  children,
  value,
  flyout,
  disabled = false,
  size = "md",
}: Readonly<MenuFlyoutProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef,
    placement: "right",
    align: "start",
  });

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!anchorRef.current?.contains(target) && !panelRef.current?.contains(target)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, panelRef]);

  const hasAutoFocusedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasAutoFocusedRef.current = false;
      return;
    }
    if (hasAutoFocusedRef.current || style.visibility !== "visible") return;
    panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    hasAutoFocusedRef.current = true;
  }, [isOpen, style.visibility, panelRef]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen && wasOpenRef.current) anchorRef.current?.focus();
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const handleFlyoutKeyDown = (event: React.KeyboardEvent) => {
    // The panel is portaled, but React still bubbles its events to the parent menu, which handles the same keys
    // and would move focus there instead. Keep list navigation inside the flyout.
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) event.stopPropagation();
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
    if (items.length === 0) return;
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        items[Math.min(currentIndex + 1, items.length - 1)]?.focus();
        break;
      case "ArrowUp":
        event.preventDefault();
        items[Math.max(currentIndex - 1, 0)]?.focus();
        break;
      case "Home":
        event.preventDefault();
        items[0]?.focus();
        break;
      case "End":
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        ref={anchorRef}
        role="menuitem"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((open) => !open);
        }}
        className="group block w-full cursor-pointer border-0 bg-transparent p-0 text-left focus:outline-none disabled:cursor-not-allowed"
      >
        <MenuItem
          size={size}
          leading={icon}
          disabled={disabled}
          trailing={
            // The value is information (the current setting), so it needs text contrast (4.5:1); the chevron is a
            // non-text mark and keeps the quieter quaternary tone (3:1).
            <span className="flex shrink-0 items-center gap-1">
              {value !== undefined && <span className="text-content-tertiary">{value}</span>}
              <ChevronRight size={size === "sm" ? 12 : 16} className="shrink-0 text-content-quaternary" />
            </span>
          }
        >
          {children}
        </MenuItem>
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            style={style}
            onKeyDown={handleFlyoutKeyDown}
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('[role="menuitem"]')) setIsOpen(false);
            }}
            className="z-[110] min-w-40 max-w-72 max-h-[min(28rem,calc(100dvh-1rem))] overflow-y-auto overscroll-contain rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg outline-none"
          >
            {flyout}
          </div>,
          document.body,
        )}
    </div>
  );
}
